import type { ReactNode } from 'react';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

import { Pressable } from '../../primitives/Pressable';
import { View } from '../../primitives/View';
import { cn } from '../../utils/cn';
import {
  isFixedSize,
  resolveBackArrowPress,
  resolveDismissKeyAction,
  shouldScroll,
} from './BottomSheet.layout';
import type { BottomSheetHandle, BottomSheetProps } from './BottomSheet.types';
import { BOTTOM_SHEET_SCREEN_INSET_PX } from './BottomSheet.variants';
import { useBottomSheetInternalContext } from './BottomSheetContext';
import { splitBottomSheetChildren } from './BottomSheetSlots';
import { BottomSheetSurface } from './BottomSheetSurface';
import { useDismissKey } from './dismiss-key';

/** Enter/exit duration of the slide, in ms. */
const TRANSITION_MS = 240;

/** Figma's sheet width (the component is 380 wide on a 393pt frame). */
const MAX_WIDTH_PX = 380;

/** Refcount so stacked sheets don't fight over `document.body.style.overflow`. */
let scrollLockCount = 0;

/**
 * Bottom sheet — WEB. A portalled, bottom-anchored dialog rendering the same
 * {@link BottomSheetSurface} the native shell does.
 *
 * Deliberately dependency-free (no Radix Dialog, no framer-motion) — the shared
 * package keeps its dependency surface minimal, the same call SegmentedControl
 * and Tag make for their animations. What that buys and what it costs:
 *
 * - **Stacking** works for free: each open sheet portals into `document.body`
 *   in mount order, so a sheet opened from inside another paints on top of it
 *   and reveals it again on dismiss. The scroll lock is refcounted.
 * - **Dismissal** is backdrop press, `Escape`, the ✕ button, or `onOpenChange`.
 *   There is **no drag-to-dismiss on web** — that gesture is the native shell's
 *   (gorhom's) job; a pointer drag adds nothing on a desktop pointer.
 * - **No focus trap.** The dialog is marked `role="dialog" aria-modal="true"`,
 *   but focus is not confined. Web callers that need a hard trap should keep
 *   using the app's Radix `Dialog` for that flow.
 *
 * The native-only knobs (`keyboardBehavior`, `stackBehavior`, the panning
 * gestures, `backdropProps`, `onChange`, …) are accepted and ignored here so a
 * cross-platform call site compiles unchanged.
 */
export const BottomSheet = forwardRef<BottomSheetHandle, BottomSheetProps>(
  function BottomSheet(
    {
      open: openProp,
      onOpenChange,
      size = 'auto',
      expandable = false,
      defaultExpanded = false,
      dismissOnBackdropPress = true,
      scroll = 'auto',
      attached = false,
      padding = 'default',
      backdropAccessibilityLabel = 'Close',
      onClose,
      onDismiss,
      contentContainerStyle,
      // No drag on web — read only as the "no casual dismiss" signal Escape
      // honours (see `resolveDismissKeyAction`).
      enablePanDownToClose,
      // Native-only — accepted so shared call sites typecheck, no-ops on web.
      enableContentPanningGesture: _enableContentPanningGesture,
      enableHandlePanningGesture: _enableHandlePanningGesture,
      stackBehavior: _stackBehavior,
      inline = false,
      keyboardApproach: _keyboardApproach,
      keyboardBehavior: _keyboardBehavior,
      keyboardBlurBehavior: _keyboardBlurBehavior,
      staticFooter: _staticFooter,
      maxDynamicContentSize: _maxDynamicContentSize,
      remeasureKey: _remeasureKey,
      backgroundStyle: _backgroundStyle,
      backdropProps: _backdropProps,
      onChange: _onChange,
      ...surfaceProps
    },
    ref,
  ) {
    // A `BottomSheetRoot` ancestor (compound / imperative usage) owns the open
    // state when the caller didn't pass `open` directly.
    const rootContext = useBottomSheetInternalContext();
    const [imperativeOpen, setImperativeOpen] = useState(false);
    const open = openProp ?? imperativeOpen;

    // Keep the sheet mounted through the exit transition.
    const [mounted, setMounted] = useState(open);
    // Drives the enter/exit transform; flipped a frame after mount.
    const [entered, setEntered] = useState(false);
    const [expanded, setExpanded] = useState(expandable && defaultExpanded);

    // Whether the sheet is open right now, for `close`. Synced in the layout
    // phase, before an enclosing Root's own layout effect drives it.
    const openRef = useRef(open);
    useLayoutEffect(() => {
      openRef.current = open;
    }, [open]);

    // Closing a sheet that is not open does nothing — as gorhom's `dismiss`
    // does on native. Without it a controlled Root mounted `open={false}`
    // (which dismisses its sheet on mount) reported a close nobody made, and
    // the host's own `open={false}` after a close reported it a second time.
    // `closing` guards re-entry: a Root's close calls back into this sheet's
    // `dismiss`, which is this same function.
    // A CONTROLLED sheet is closed only when its `open` prop says so: its
    // parent may refuse the close, and a sheet that marked itself closed
    // anyway would ignore every later close until `open` toggled.
    const controlled = openProp !== undefined;
    const closing = useRef(false);
    const close = useCallback(() => {
      if (closing.current || !openRef.current) return;
      closing.current = true;
      if (!controlled) openRef.current = false;
      try {
        setImperativeOpen(false);
        onOpenChange?.(false);
        rootContext?.onDismiss();
      } finally {
        closing.current = false;
      }
    }, [controlled, onOpenChange, rootContext]);

    const handle = useCallback<() => BottomSheetHandle>(
      () => ({
        present: () => {
          setImperativeOpen(true);
          onOpenChange?.(true);
        },
        dismiss: close,
        close,
        expand: () => setExpanded(true),
        collapse: () => setExpanded(false),
        // The web body is a plain overflow container, not a scroll view with
        // an imperative API — parity would need a ref chain for no gain.
        scrollToEnd: () => {},
        scrollToTop: () => {},
      }),
      [close, onOpenChange],
    );

    useImperativeHandle(ref, handle, [handle]);

    // Publish the same handle to an enclosing Root so `useBottomSheetRef()`
    // and `<BottomSheetTrigger>` drive this sheet.
    const rootSheetRef = rootContext?.sheetRef;
    // Layout phase: the Root presents a controlled `open` in ITS layout effect,
    // which runs after this one — a plain effect would land too late and a
    // sheet mounted already-open would never show.
    useLayoutEffect(() => {
      if (!rootSheetRef) return;
      rootSheetRef.current = handle();
      return () => {
        rootSheetRef.current = null;
      };
    }, [rootSheetRef, handle]);

    useEffect(() => {
      if (open) {
        setMounted(true);
        setExpanded(expandable && defaultExpanded);
        // Two frames: one for the portal to paint at the off-screen offset,
        // one for the transition to have something to animate from.
        const frame = requestAnimationFrame(() =>
          requestAnimationFrame(() => setEntered(true)),
        );
        return () => cancelAnimationFrame(frame);
      }
      setEntered(false);
      const timer = setTimeout(() => {
        setMounted(false);
        onDismiss?.();
      }, TRANSITION_MS);
      return () => clearTimeout(timer);
      // `defaultExpanded` / `expandable` are read as the sheet opens, not tracked.
    }, [open]);

    // Escape acts on the TOPMOST sheet only (the shared dismiss-key stack, the
    // same one Android's hardware Back walks), so it steps a stack down one
    // level at a time. What it does — step back, close, or hold a sheet that
    // opted out of a casual dismiss — is `resolveDismissKeyAction`, shared
    // with native.
    const backArrowPress = resolveBackArrowPress(
      splitBottomSheetChildren(surfaceProps.children).back,
      surfaceProps.onBack,
    );
    // An `inline` sheet is always mounted (its body survives a close), so
    // what "is up" means for Escape and the scroll lock is `open`, not
    // `mounted`.
    const shown = inline || mounted;
    const active = inline ? open : mounted;
    useDismissKey(active, () => {
      const action = resolveDismissKeyAction({
        leaving: !open,
        canStepBack: backArrowPress != null,
        enablePanDownToClose: enablePanDownToClose ?? true,
        dismissOnBackdropPress,
      });
      if (action === 'step-back') backArrowPress?.();
      else if (action === 'close') close();
    });

    // Refcounted body scroll lock so stacked sheets restore it exactly once.
    useEffect(() => {
      if (!active || typeof document === 'undefined') return;
      const previous = document.body.style.overflow;
      scrollLockCount += 1;
      document.body.style.overflow = 'hidden';
      return () => {
        scrollLockCount -= 1;
        if (scrollLockCount === 0) document.body.style.overflow = previous;
      };
    }, [active]);

    if (!shown || typeof document === 'undefined') return null;

    const fixedSize = isFixedSize(size);
    // Scrollable by DEFAULT whenever the sheet is or can become bounded —
    // mirrors the native shell.
    const scrollEnabled = shouldScroll(scroll, fixedSize || expandable);
    const bodyWrapper = scrollEnabled
      ? (body: ReactNode) => (
          <View
            className="min-h-0 w-full flex-1 overflow-y-auto"
            webProps={{ style: contentContainerStyle }}>
            {body}
          </View>
        )
      : contentContainerStyle
        ? (body: ReactNode) => (
            <View className="w-full" webProps={{ style: contentContainerStyle }}>
              {body}
            </View>
          )
        : undefined;

    // `size` on web is a max-height cap on the card, since the page has no
    // snap points; `expanded` promotes it to a hard height.
    const sizeHeight =
      size === 'full'
        ? '100%'
        : size === 'half'
          ? '50%'
          : typeof size === 'number'
            ? size
            : undefined;

    const sheet = (
      <View
        // The View primitive is `flex-col`, so bottom-anchoring is
        // `justify-end` and horizontal centering is `items-center`. The screen
        // inset is padding here rather than a margin on the card — a margin
        // would stack with the card's own `w-full` and push it off-centre.
        //
        // Click-through while NOT entered: the sheet reports its close at the
        // START of the exit but stays mounted for the whole slide, and this
        // full-viewport layer ate the click on whatever the user reached for
        // next (the native shell's backdrop has the same rule — see
        // `backdropPointerEvents`). The two entry frames are covered too,
        // harmlessly.
        className={cn(
          // In place: covers the host's container, not the viewport.
          inline ? 'absolute' : 'fixed',
          'inset-0 z-50 flex flex-col items-center justify-end',
          !entered && 'pointer-events-none',
        )}
        webProps={{
          role: 'dialog',
          'aria-modal': true,
          // A closed `inline` sheet is still in the DOM — and out of the a11y tree.
          'aria-hidden': inline && !open ? true : undefined,
          // `attached` drops the screen gap so the card lands on the viewport
          // edges — the same opt-out the native shell honours.
          style: { padding: attached ? 0 : BOTTOM_SHEET_SCREEN_INSET_PX },
        }}>
        <Pressable
          as="div"
          onPress={dismissOnBackdropPress ? close : undefined}
          accessibilityLabel={backdropAccessibilityLabel}
          className={cn(
            'absolute inset-0 bg-black/40 transition-opacity motion-reduce:transition-none',
            entered ? 'opacity-100' : 'opacity-0',
          )}
          webProps={{ style: { transitionDuration: `${TRANSITION_MS}ms` } }}
        />
        <div
          className={cn(
            'relative box-border flex min-h-0 w-full min-w-0 flex-col transition-transform motion-reduce:transition-none',
            entered ? 'translate-y-0' : 'translate-y-full',
          )}
          style={{
            maxWidth: MAX_WIDTH_PX,
            maxHeight: '100%',
            height: expanded ? '100%' : sizeHeight,
            transitionDuration: `${TRANSITION_MS}ms`,
          }}>
          <BottomSheetSurface
            {...surfaceProps}
            // Attached: the card's bottom edge IS the viewport edge, so the
            // bottom corners square off (native does the same in its chrome).
            className={cn(attached && 'rounded-b-none', surfaceProps.className)}
            padding={padding}
            fill
            // Web already applies `padding` on the Surface (outside the slot),
            // so only the fades need wiring — the Surface nests the scrolling
            // wrapper inside the content slot on both platforms.
            //
            // `?? `, not a bare override: this sits AFTER `{...surfaceProps}`,
            // so assigning it unconditionally swallowed a caller's own
            // `contentScrolls` — the prop a `scroll="self"` body uses to say
            // "there IS a scroller in here, draw the edge fades".
            contentScrolls={surfaceProps.contentScrolls ?? scrollEnabled}
            bodyWrapper={bodyWrapper}
            onClose={onClose ?? close}
          />
        </div>
      </View>
    );
    return inline ? sheet : createPortal(sheet, document.body);
  },
);
